import type { Variants } from "motion/react";
import { useAnimation } from "motion/react";
import * as m from "motion/react-m";
import { forwardRef, useImperativeHandle } from "react";

import { cn } from "@/shared/lib";
import type { AnimatedIconProps } from "@/shared/ui/animated-icon";

export interface LanguagesIconHandle {
  startAnimation: () => void;
  stopAnimation: () => void;
}

const PATH_VARIANTS: Variants = {
  normal: { opacity: 1, pathLength: 1, pathOffset: 0 },
  animate: (custom: number) => ({
    opacity: [0, 1],
    pathLength: [0, 1],
    pathOffset: [1, 0],
    transition: {
      opacity: { duration: 0.01, delay: custom * 0.1 },
      pathLength: {
        type: "spring",
        duration: 0.5,
        bounce: 0,
        delay: custom * 0.1,
      },
    },
  }),
};

const SVG_VARIANTS: Variants = {
  normal: { opacity: 1 },
  animate: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.2,
    },
  },
};

const LanguagesIcon = forwardRef<LanguagesIconHandle, AnimatedIconProps>(
  ({ className, size = 24, ...props }, ref) => {
    const svgControls = useAnimation();
    const pathControls = useAnimation();

    useImperativeHandle(ref, () => {
      return {
        startAnimation: () => {
          svgControls.start("animate");
          pathControls.start("animate");
        },
        stopAnimation: () => {
          svgControls.start("normal");
          pathControls.start("normal");
        },
      };
    });

    return (
      <m.svg
        className={cn("lucide", className)}
        {...props}
        animate={svgControls}
        fill="none"
        height={size}
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2"
        variants={SVG_VARIANTS}
        viewBox="0 0 24 24"
        width={size}
        xmlns="http://www.w3.org/2000/svg"
      >
        <m.path
          animate={pathControls}
          custom={3}
          d="m5 8 6 6"
          variants={PATH_VARIANTS}
        />
        <m.path
          animate={pathControls}
          custom={2}
          d="m4 14 6-6 3-3"
          variants={PATH_VARIANTS}
        />
        <m.path
          animate={pathControls}
          custom={1}
          d="M2 5h12"
          variants={PATH_VARIANTS}
        />
        <m.path
          animate={pathControls}
          custom={0}
          d="M7 2h1"
          variants={PATH_VARIANTS}
        />
        <m.path
          animate={pathControls}
          custom={3}
          d="m22 22-5-10-5 10"
          variants={PATH_VARIANTS}
        />
        <m.path
          animate={pathControls}
          custom={3}
          d="M14 18h6"
          variants={PATH_VARIANTS}
        />
      </m.svg>
    );
  }
);

LanguagesIcon.displayName = "LanguagesIcon";

export { LanguagesIcon };
